/**
 * Dashboard Page Component
 * 
 * Main dashboard page for authenticated users.
 * Displays user information, competitions, and quick actions.
 */

import React, { useMemo, useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { authUtils } from '../services/auth';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Header from '../components/common/Header';
import ListControls from '../components/common/ListControls';
import PageTitle from '../components/common/PageTitle';
import YourCompetitions from './dashboard/YourCompetitions';
import { useAccountCounts } from '../components/account/useAccountCounts';
import { Competition } from '../types/interfaces';
import CompetitionTypePill from '../components/common/CompetitionTypePill';
import CompetitionVisibilityPill from '../components/common/CompetitionVisibilityPill';
import './Dashboard.css';
import { useDocumentTitle } from '../hooks/useDocumentTitle';



/**
 * Dashboard page component
 * @returns {JSX.Element} Dashboard page component
 */
const Dashboard: React.FC = () => {
  useDocumentTitle('Dashboard');
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const { data, loading: countsLoading } = useAccountCounts();

  // Load competitions data
  useEffect(() => {
    const loadCompetitions = async () => {
      try {
        setLoading(true);
        const token = authUtils.getStoredToken();
        const response = await apiService.post('/competitions/list', {}, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setCompetitions(response.data);
      } catch (err) {
        setError('Failed to load competitions');
        console.error('Error loading competitions:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCompetitions();
  }, []);

  const typeOptions = useMemo(() => {
    const normalizedTypes = competitions
      .map((c) => (c.type || 'unknown').toLowerCase())
      .filter((value, index, all) => all.indexOf(value) === index)
      .sort((a, b) => a.localeCompare(b));

    return [
      { label: 'All types', value: 'all' },
      ...normalizedTypes.map((type) => ({
        label: type === 'unknown' ? 'Unknown' : type.toUpperCase(),
        value: type,
      })),
    ];
  }, [competitions]);

  const filteredCompetitions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return competitions.filter((c) => {
      const matchesQuery =
        !q ||
        (c.title || '').toLowerCase().includes(q) ||
        (c.description || '').toLowerCase().includes(q);
      const status = (c.status || '').toLowerCase();
      const matchesStatus = statusFilter === 'all' || status === statusFilter;
      const type = (c.type || 'unknown').toLowerCase();
      const matchesType = typeFilter === 'all' || type === typeFilter;
      return matchesQuery && matchesStatus && matchesType;
    });
  }, [competitions, search, statusFilter, typeFilter]);

  return (
    <div className="dashboard">
      <Header />

      {/* Main Content */}
      <main className="dashboard__main">
        <div className="container">
          <PageTitle title="Competitions" />
          <YourCompetitions
            data={data}
            loading={countsLoading}
            onBrowse={() => document.getElementById('all-competitions')?.scrollIntoView({ behavior: 'smooth' })}
          />

          {/* Competitions Section */}
          <section className="dashboard__competitions">
            <div className="dashboard__section-header">
              <h2 id="all-competitions" className="dashboard__list-title">All competitions</h2>
            </div>

            {!loading && !error && (
              <ListControls
                searchValue={search}
                onSearchChange={setSearch}
                searchPlaceholder="Search competitions..."
                sticky
                statusValue={statusFilter}
                onStatusChange={setStatusFilter}
                statusOptions={[
                  { label: 'All', value: 'all' },
                  { label: 'Open', value: 'open' },
                  { label: 'Closed', value: 'closed' },
                  { label: 'Archived', value: 'archived' },
                  { label: 'New', value: 'new' },
                ]}
                typeValue={typeFilter}
                onTypeChange={setTypeFilter}
                typeOptions={typeOptions}
              />
            )}

            {loading ? (
              <div className="dashboard__loading">
                <LoadingSpinner size="md" text="Loading competitions..." />
              </div>
            ) : error ? (
              <div className="dashboard__error">
                <p>{error}</p>
                <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
                  Retry
                </Button>
              </div>
            ) : filteredCompetitions.length === 0 ? (
              <p className="dashboard__empty">
                No competitions match these filters.{' '}
                {(search || statusFilter !== 'all' || typeFilter !== 'all') && (
                  <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setStatusFilter('all'); setTypeFilter('all'); }}>Clear filters</Button>
                )}
              </p>
            ) : (
              <div className="dashboard__competitions-grid" data-tour="competition-grid">
                {filteredCompetitions.map((competition) => {
                  const isTeamMode = (competition.competition_mode || 'individual') === 'team';
                  const hasMoneyPrize = (competition.prize || '').toLowerCase() === 'money';
                  const participantUnitLabel =
                    competition.participants === 1
                      ? (isTeamMode ? 'team' : 'participant')
                      : (isTeamMode ? 'teams' : 'participants');

                  return (
                    <div key={competition.id} className="dashboard__competition-card">
                      <div className="dashboard__competition-header dashboard__competition-header--with-logo">
                        {competition.logo_path && (
                          <img
                            src={`${competition.logo_path}`}
                            alt={`${competition.title} logo`}
                            className="dashboard__competition-logo"
                          />
                        )}
                        <h4>{competition.title}</h4>
                      </div>
                      <p className="dashboard__competition-description">
                        {competition.description && competition.description.length > 300
                          ? competition.description.slice(0, 300) + '...'
                          : competition.description}
                      </p>
                      <div className="dashboard__competition-meta-block">
                        {(competition.participants !== undefined || hasMoneyPrize) && (
                          <div className="dashboard__competition-meta">
                            {competition.participants !== undefined && (
                              <div className="dashboard__competition-participants">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                  <circle cx="9" cy="7" r="4" />
                                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                                {competition.participants} {participantUnitLabel}
                              </div>
                            )}
                            {hasMoneyPrize && (
                              <span
                                className="dashboard__competition-prize-inline"
                                title="Money prize"
                                aria-label="Money prize"
                              >
                                $$
                              </span>
                            )}
                          </div>
                        )}
                        <div className="dashboard__competition-type">
                          <span className={`dashboard__competition-status dashboard__competition-status--${competition.status}`}>
                            {competition.status}
                          </span>
                          <CompetitionTypePill type={competition.type} />
                          <CompetitionVisibilityPill visibility={competition.visibility} />
                        </div>
                      </div>
                      <div className="dashboard__competition-actions">
                        <Button variant="primary" size="sm" onClick={() => window.location.href = `/competition/${competition.id}`}>
                          {/*{competition.status === 'open' ? 'Join Competition' : 'View Details'}*/}
                          Open
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>


        </div>
      </main>
    </div>
  );
};

export default Dashboard;
